import { supabase } from "./supabase";

/**
 * Address → { lat, lon, source } resolver. Three-tier strategy, fast-path first:
 *
 *   1. `parcels` table  — instant when the address is in the 7 GA counties
 *                          we've already loaded from Regrid (Bartow, Cherokee,
 *                          Cobb, DeKalb, Forsyth, Fulton, Gwinnett). 1.46M rows.
 *   2. Mapbox forward    — VITE_MAPBOX_TOKEN required. Better for fresh
 *                          construction / addresses Regrid is behind on.
 *   3. Nominatim (OSM)   — CORS-enabled, no key required. Always-available
 *                          fallback. Slower (~600ms) and rate-limited; courtesy
 *                          use only.
 *
 * Returns null when ALL three sources fail. Callers should treat null as a hard
 * stop — without coords the deal can't be flown, mapped, or storm-reported.
 *
 * Used by `ManualEntryModal` (block creation without coords) and any future
 * "Find in canvas" recovery affordance.
 */

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

/**
 * Escape `%` and `_` in user input so they're treated as literal characters
 * by PostgreSQL's ILIKE operator (default escape char is `\`). Without this,
 * a homeowner address `100% Pure Lane` would match every row starting with
 * `100` — false-positive dupe blocks and wrong parcel hits.
 */
function escLike(s: string): string {
  return s.replace(/[\\%_]/g, "\\$&");
}

export type GeocodeSource = "parcels" | "mapbox" | "nominatim";

export type GeocodeHit = {
  lat: number;
  lon: number;
  source: GeocodeSource;
  label?: string;
};

export async function geocodeAddress(
  street: string,
  city: string,
  state = "GA",
  zip = "",
): Promise<GeocodeHit | null> {
  const cleanStreet = (street ?? "").trim();
  const cleanCity = (city ?? "").trim();
  const cleanState = (state ?? "GA").trim().toUpperCase();
  const cleanZip = (zip ?? "").trim();
  if (!cleanStreet || !cleanCity) return null;

  // 1) Parcels — fastest, most accurate when we have it.
  try {
    const { data } = await supabase
      .from("parcels")
      .select("lat,lon,street,city,state,zip")
      // Trailing wildcard kept (un-escaped) so "1905 Glenellen Ct" still
      // matches the parcels row "1905 GLENELLEN CT NW". User input is
      // escaped to prevent stray `%`/`_` from acting as wildcards.
      .ilike("street", `${escLike(cleanStreet)}%`)
      .ilike("city", escLike(cleanCity))
      .eq("state", cleanState)
      .not("lat", "is", null)
      .limit(1);
    if (data && data.length > 0 && data[0].lat != null && data[0].lon != null) {
      const row = data[0] as {
        lat: string | number;
        lon: string | number;
        street: string;
        city: string;
        state: string;
        zip: string | null;
      };
      // PostgREST returns numeric columns as strings — coerce and guard
      // against NaN before stamping coords on a real deal.
      const lat = Number(row.lat);
      const lon = Number(row.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        return {
          lat,
          lon,
          source: "parcels",
          label: `${row.street}, ${row.city}, ${row.state}${row.zip ? " " + row.zip : ""}`,
        };
      }
      // Bad parcel data — fall through to next tier.
    }
  } catch {
    // Network/RLS errors are swallowed — fall through to next tier.
  }

  const q = [cleanStreet, cleanCity, cleanState, cleanZip]
    .filter(Boolean)
    .join(", ");

  // 2) Mapbox forward geocoder.
  if (MAPBOX_TOKEN) {
    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json` +
        `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}&country=us&limit=1&types=address`;
      const r = await fetch(url);
      if (r.ok) {
        const j = (await r.json()) as {
          features?: Array<{ center: [number, number]; place_name: string }>;
        };
        const f = j.features?.[0];
        if (f && Array.isArray(f.center) && f.center.length === 2) {
          const lat = Number(f.center[1]);
          const lon = Number(f.center[0]);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            return { lat, lon, source: "mapbox", label: f.place_name };
          }
        }
      }
    } catch {
      // fall through
    }
  }

  // 3) Nominatim — last-resort, no API key required.
  try {
    const url =
      "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
      encodeURIComponent(q);
    const r = await fetch(url, { headers: { Accept: "application/json" } });
    if (r.ok) {
      const arr = (await r.json()) as Array<{
        lat: string;
        lon: string;
        display_name: string;
      }>;
      if (arr && arr.length > 0) {
        const lat = parseFloat(arr[0].lat);
        const lon = parseFloat(arr[0].lon);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          return { lat, lon, source: "nominatim", label: arr[0].display_name };
        }
      }
    }
  } catch {
    // fall through
  }

  return null;
}

/** Stages that count as "terminal" — a new deal at the same address is OK. */
export const TERMINAL_DEAL_STAGES = [
  "no_thanks",
  "insufficient_damage",
  "claims_denied",
  "cancelled",
  "closed_won",
  "closed_lost",
  "closed_inspection",
] as const;

export type DuplicateDealHit = {
  id: string;
  homeowner_name: string | null;
  stage: string;
  owner_id: string | null;
  created_at: string;
};

/**
 * Returns active deals at the same (street, city). Mirrors the dedup rule used
 * by `sr-intake` v30 (address is the dedup key — never name/phone, since one
 * homeowner can own multiple properties). Terminal-stage deals are NOT
 * returned — a homeowner can legitimately re-appoint a year later.
 *
 * Empty array = safe to create. Non-empty = surface to the user for
 * confirmation before creating a possible duplicate.
 */
export async function findActiveDealsAtAddress(
  street: string,
  city: string,
): Promise<DuplicateDealHit[]> {
  const cleanStreet = (street ?? "").trim();
  const cleanCity = (city ?? "").trim();
  if (!cleanStreet || !cleanCity) return [];
  try {
    const { data } = await supabase
      .from("deals")
      .select("id, homeowner_name, stage, owner_id, created_at")
      // Exact case-insensitive match — escape wildcards in user input so
      // a deal at "100% Pure Lane" doesn't false-match every "100 …".
      .ilike("property_street", escLike(cleanStreet))
      .ilike("property_city", escLike(cleanCity))
      .not(
        "stage",
        "in",
        `(${TERMINAL_DEAL_STAGES.map((s) => `"${s}"`).join(",")})`,
      )
      .order("created_at", { ascending: false })
      .limit(5);
    return (data ?? []) as DuplicateDealHit[];
  } catch {
    return [];
  }
}
